import { ColumnDef } from '@tanstack/react-table';

import { djs } from '@/lib/formatters';
import { Badge, BadgeProps } from '@/components/ui/badge';
import { DataTableColumnHeader } from '@/components/data-table-column-header';
import { Status } from '@/models/campaign';
import type { Promotion } from '@/models/promotion';
import { truncateString } from '@/utils';
import { DATE_SHORT_FORMAT } from '@/lib/formatters';
import ShowHideDisplay from '../ShowHideDisplay';
import { TableThumbnail } from './table-thumbnail';

const statusBadgeMap: Record<Status, BadgeProps['variant']> = {
  scheduled: 'secondary',
  live: 'default',
  completed: 'destructive',
  draft: 'outline',
  booked: 'outline',
  dark: 'secondary',
  deleted: 'destructive',
  reserved: 'outline',
};

const statusLabelMap: Record<Status, string> = {
  scheduled: 'Scheduled',
  live: 'Active',
  completed: 'Ended',
  draft: 'Draft',
  booked: 'Booked',
  dark: 'Dark',
  deleted: 'Deleted',
  reserved: 'Reserved',
};

export const columns: ColumnDef<Promotion>[] = [
  {
    meta: { label: 'Name' },
    accessorKey: 'name',
    header: 'Name',
    cell: function RenderCell({ row }) {
      const item = row.original;
      const thumbnail = item.creative.assets.find(
        (a) => a.type === 'thumbnail' && a.url,
      );

      return (
        <span className="flex items-center gap-4">
          <TableThumbnail url={thumbnail?.url} />
          <span>{truncateString(item.name)}</span>
        </span>
      );
    },
  },
  {
    meta: { label: 'Status' },
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status as Status;
      const variant = statusBadgeMap[status];

      return (
        <Badge className="capitalize" variant={variant ?? 'outline'}>
          {statusLabelMap[status]}
        </Badge>
      );
    },
  },
  {
    meta: { label: 'Zones' },
    accessorKey: 'zone_placements',
    header: 'Zones',
    cell: ({ row }) => {
      const item = row.original;
      const { zones } = item.targeting;

      if (zones.length === 0) return null;

      return <ShowHideDisplay key={item.id} data={zones} />;
    },
  },
  {
    meta: { label: 'Frequency' },
    accessorKey: 'boost_multiplier',
    header: 'Frequency',
    cell: ({ row }) => {
      const boostMultiplier = row.original.boost_multiplier;

      return (
        <span className="capitalize">{boostMultiplier?.name ?? 'normal'}</span>
      );
    },
  },
  {
    meta: { label: 'Start Date' },
    accessorKey: 'duration.starts_at',
    header: () => (
      <DataTableColumnHeader sortKey="starts_at" title="Start Date" />
    ),
    cell: ({ row }) => {
      return djs(row.original.duration.starts_at).format(DATE_SHORT_FORMAT);
    },
  },
  {
    meta: { label: 'End Date' },
    accessorKey: 'duration.ends_at',
    header: () => <DataTableColumnHeader sortKey="ends_at" title="End Date" />,
    cell: ({ row }) => {
      const endDate = row.original.duration.ends_at;

      return endDate ? djs(endDate).format(DATE_SHORT_FORMAT) : 'No End Date';
    },
  },
];
